import { inject, Injectable } from '@angular/core';
import { IAuthenticatedUser, IAuthDto } from '@domain/auth';
import { catchError, Observable, of, switchMap, tap, throwError, timeout } from 'rxjs';
import { ApiService } from '@core/api';
import { AuthStore } from '@feature/auth/data';
import { MaintenanceService } from '@infrastructure/maintenance';
import { TelegramService, WriteAccessService } from '@infrastructure/telegram';

type AuthenticateOptions = {
  handleWriteAccess?: boolean;
};

const AUTH_REQUEST_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  readonly #api = inject(ApiService);
  readonly #authStore = inject(AuthStore);
  readonly #maintenance = inject(MaintenanceService);
  readonly #telegram = inject(TelegramService);
  readonly #writeAccess = inject(WriteAccessService);

  public authenticate(dto: IAuthDto, options: AuthenticateOptions = {}): Observable<IAuthenticatedUser> {
    const shouldHandleWriteAccess = options.handleWriteAccess !== false;

    return this.#authenticateRequest(dto).pipe(
      switchMap(user => {
        if (!shouldHandleWriteAccess || user.maintenance_popup?.show === true) {
          return of(user);
        }

        return this.#handleWriteAccessIfNeeded$(user, dto);
      }),
      tap(user => {
        this.#authStore.setUser(user);
        this.#maintenance.syncWithAuthPopup(user.maintenance_popup);
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  #authenticateRequest(dto: IAuthDto): Observable<IAuthenticatedUser> {
    return this.#api.post<IAuthenticatedUser>('/users/authenticate', dto).pipe(
      timeout({
        first: AUTH_REQUEST_TIMEOUT_MS,
        with: () => throwError(() => ({ code: 'PG_AUTH_TIMEOUT' })),
      })
    );
  }

  #handleWriteAccessIfNeeded$(user: IAuthenticatedUser, dto: IAuthDto): Observable<IAuthenticatedUser> {
    if (!this.#writeAccess.hasReason(user, 'write_access_not_granted')) {
      return of(user);
    }

    return this.#writeAccess.requestAccess$().pipe(
      switchMap(allowed => {
        if (!allowed) {
          return of(user);
        }

        return this.#authenticateRequest({
          init_data: this.#telegram.initData || dto.init_data,
        });
      }),
      catchError(error => {
        console.error('[PG][Auth][write-access-flow-error]', error);
        return of(user);
      })
    );
  }
}
